// src/config/theme.ts

/**
 * Mythbound™ Theme Configuration
 * 
 * This file contains the theme configuration for the Mythbound™ web application,
 * including colors, typography, and other design elements based on the provided
 * UI/UX requirements.
 */

export const theme = {
  // Color Palette
  colors: {
    // Primary Colors
    primary: {
      main: '#3BE8FF', // Mythic Blue - bright glowing blue from the dice/symbols
      dark: '#111116', // Dark Abyss - deep background black
      gold: '#D9B555', // Arcane Gold - metallic gold accents
    },
    
    // Secondary Colors
    secondary: {
      purple: '#6B4FBB', // Mystic Purple - from dragon wings
      white: '#F5F5F7',  // Wizard White - text and highlights
      parchment: '#E8D9B0', // Scroll Parchment - for content areas
    },
    
    // Accent Colors
    accent: {
      teal: '#35889A',   // Enchanted Teal - darker blue accent
      silver: '#C5C5C7', // Runic Silver - metallic silver accents
      brown: '#423C37',  // Dragon Scale - dark brown accent
    },
    
    // Functional Colors
    functional: {
      success: '#4CAF50',
      error: '#FF5252',
      warning: '#FFC107',
      info: '#2196F3',
    },
    
    // Background Gradients
    gradients: {
      darkPortal: 'radial-gradient(circle, #1E1E24 0%, #111116 100%)',
      magicGlow: 'linear-gradient(135deg, #3BE8FF 0%, #6B4FBB 100%)',
      parchment: 'linear-gradient(to bottom, #E8D9B0 0%, #D9C9A0 100%)',
    },
  },
  
  // Typography
  typography: {
    // Font Families
    fontFamily: {
      heading: "'Cinzel', 'Times New Roman', serif",
      body: "'Montserrat', 'Arial', 'Helvetica', sans-serif",
    },
    
    // Font Weights
    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      bold: 700,
    },
    
    // Font Sizes
    fontSize: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      md: '1rem',       // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      '2xl': '1.5rem',  // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem',  // 36px
      '5xl': '3rem',     // 48px
    },
  },
  
  // Spacing
  spacing: {
    xs: '0.25rem',  // 4px
    sm: '0.5rem',   // 8px
    md: '1rem',     // 16px
    lg: '1.5rem',   // 24px
    xl: '2rem',     // 32px
    '2xl': '3rem',  // 48px
    '3xl': '4rem',  // 64px
  },
  
  // Borders
  borders: {
    radius: {
      sm: '0.25rem',  // 4px
      md: '0.5rem',   // 8px
      lg: '1rem',     // 16px
      full: '9999px', // Circular
    },
    width: {
      thin: '1px',
      medium: '2px',
      thick: '4px',
    },
  },
  
  // Shadows
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
    md: '0 4px 6px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.24)',
    lg: '0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23)',
    xl: '0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22)',
    glow: '0 0 15px rgba(59, 232, 255, 0.7)',
    goldGlow: '0 0 10px rgba(217, 181, 85, 0.7)',
  },
  
  // Z-index
  zIndex: {
    base: 0,
    dropdown: 1000,
    sticky: 1100,
    fixed: 1200,
    modalBackdrop: 1300,
    modal: 1400,
    popover: 1500,
    tooltip: 1600,
  },
  
  // Transitions
  transitions: {
    duration: {
      fast: '150ms',
      normal: '300ms',
      slow: '500ms',
    },
    timing: {
      ease: 'ease',
      easeIn: 'ease-in',
      easeOut: 'ease-out',
      easeInOut: 'ease-in-out',
    },
  },
  
  // Breakpoints for responsive design
  breakpoints: {
    xs: '0px',
    sm: '600px',
    md: '960px',
    lg: '1280px',
    xl: '1920px',
  },
};

export default theme;
